/**
 * Participants panel (docs/06-UI-SPEC.md §6.4, SRS D3 and D4). Teacher-side only.
 *
 * Everything here is a request to the server, which decides and then answers with the
 * whole class again — so the panel always shows what the server actually did rather
 * than what this screen hoped it would do.
 *
 * The dot is connection, not permission: green while that person's board is open,
 * grey once it is not. The reconnecting state arrives with step 3.5.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  fetchClassroom,
  removeMember,
  setClassLocked,
  setMemberEditing,
  type ApiResult,
  type ClassMember,
  type Classroom,
} from '../../api.js';

export interface ParticipantsPanelProps {
  /** Null on a board opened without a class behind it. */
  readonly boardId: string | null;
  /** Who is connected right now, by id. */
  readonly online: readonly string[];
  /** SRS D6 - whether everybody's board is following this one. */
  readonly presenting: boolean;
  readonly onPresentingChange: (presenting: boolean) => void;
}

const EMPTY: Classroom = { locked: false, members: [] };

const ACTION =
  'h-touch flex-1 rounded-md border px-2 text-sm transition-colors duration-fast disabled:opacity-50';

/** Asks the server for the class, and again whenever the people on the board change. */
function useClassroom(boardId: string | null, online: readonly string[]) {
  const [classroom, setClassroom] = useState<Classroom>(EMPTY);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const apply = useCallback((result: ApiResult<Classroom>) => {
    setBusy(false);
    if (result.ok) {
      setClassroom(result.value);
      setProblem(null);
      return;
    }
    setProblem(result.message);
  }, []);

  useEffect(() => {
    if (!boardId) return undefined;
    let cancelled = false;
    void fetchClassroom(boardId).then((result) => {
      if (!cancelled) apply(result);
    });
    return () => {
      cancelled = true;
    };
  }, [boardId, online, apply]);

  const send = useCallback(
    (call: Promise<ApiResult<Classroom>>) => {
      setBusy(true);
      void call.then(apply);
    },
    [apply],
  );

  return { classroom, problem, busy, send };
}

/** Green while their board is open, grey once it is closed (docs/06-UI-SPEC.md §6.4). */
function StatusDot({ connected }: { readonly connected: boolean }) {
  return (
    <span
      aria-hidden
      className={`h-2 w-2 shrink-0 rounded-full ${connected ? 'bg-success' : 'bg-text-placeholder'}`}
    />
  );
}

interface MemberRowProps {
  readonly member: ClassMember;
  readonly connected: boolean;
  readonly busy: boolean;
  readonly onToggleEditing: () => void;
  readonly onRemove: () => void;
}

function MemberRow({ member, connected, busy, onToggleEditing, onRemove }: MemberRowProps) {
  return (
    <li className="flex flex-col gap-2 border-b border-border pb-3 last:border-b-0">
      <span className="flex items-center gap-2 text-base text-text-primary">
        <StatusDot connected={connected} />
        <span className="truncate">{member.displayName}</span>
      </span>
      <span className="flex gap-2">
        <button
          type="button"
          disabled={busy}
          aria-pressed={member.canEdit}
          aria-label={`${member.canEdit ? 'Stop' : 'Let'} ${member.displayName} draw`}
          onClick={onToggleEditing}
          className={`${ACTION} ${
            member.canEdit
              ? 'border-primary bg-primary-subtle text-primary'
              : 'border-border bg-surface text-text-secondary hover:bg-surface-hover'
          }`}
        >
          {member.canEdit ? 'Can draw' : 'Watching'}
        </button>
        <button
          type="button"
          disabled={busy}
          aria-label={`Remove ${member.displayName} from the class`}
          onClick={onRemove}
          className={`${ACTION} border-border bg-surface text-text-secondary hover:bg-surface-hover`}
        >
          Remove
        </button>
      </span>
    </li>
  );
}

interface LockToggleProps {
  readonly locked: boolean;
  readonly busy: boolean;
  readonly onToggle: () => void;
}

/** SRS D4 — a locked class takes no new joiners. Everyone already in stays in. */
function LockToggle({ locked, busy, onToggle }: LockToggleProps) {
  return (
    <button
      type="button"
      disabled={busy}
      aria-pressed={locked}
      onClick={onToggle}
      className={`${ACTION} w-full ${
        locked
          ? 'border-primary bg-primary-subtle text-primary'
          : 'border-border bg-surface text-text-secondary hover:bg-surface-hover'
      }`}
    >
      {locked ? 'Class is closed to new students' : 'Close the class to new students'}
    </button>
  );
}

interface MemberListProps {
  readonly classroom: Classroom;
  readonly online: readonly string[];
  readonly busy: boolean;
  readonly onEditing: (member: ClassMember) => void;
  readonly onRemove: (member: ClassMember) => void;
}

/** The teacher first, then everybody who has joined the class. */
function MemberList({ classroom, online, busy, onEditing, onRemove }: MemberListProps) {
  return (
    <ul className="flex flex-col gap-3">
      <li className="flex items-center gap-2 text-base text-text-primary">
        <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-teacher-cursor" />
        Teacher (you)
      </li>
      {classroom.members.map((member) => (
        <MemberRow
          key={member.id}
          member={member}
          connected={online.includes(member.id)}
          busy={busy}
          onToggleEditing={() => {
            onEditing(member);
          }}
          onRemove={() => {
            onRemove(member);
          }}
        />
      ))}
    </ul>
  );
}

/** SRS D6 - every student's board follows this one until it is switched off. */
function PresentToggle({
  presenting,
  onToggle,
}: {
  readonly presenting: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={presenting}
      onClick={onToggle}
      className={`${ACTION} w-full ${
        presenting
          ? 'border-primary bg-primary-subtle text-primary'
          : 'border-border bg-surface text-text-secondary hover:bg-surface-hover'
      }`}
    >
      {presenting ? 'Everyone is following your view' : 'Have everyone follow your view'}
    </button>
  );
}

export function ParticipantsPanel({
  boardId,
  online,
  presenting,
  onPresentingChange,
}: ParticipantsPanelProps) {
  const { classroom, problem, busy, send } = useClassroom(boardId, online);
  const board = boardId ?? '';

  return (
    <section className="flex w-panel shrink-0 flex-col gap-3 border-l border-t border-border bg-surface p-4">
      <h2 className="text-base font-semibold text-text-primary">
        In this class ({classroom.members.length + 1})
      </h2>
      {problem === null ? null : <p className="text-sm text-danger">{problem}</p>}

      <MemberList
        classroom={classroom}
        online={online}
        busy={busy}
        onEditing={(member) => {
          send(setMemberEditing(board, member.id, !member.canEdit));
        }}
        onRemove={(member) => {
          send(removeMember(board, member.id));
        }}
      />

      {classroom.members.length === 0 ? (
        <p className="text-sm text-text-secondary">
          Share the class code and your students will appear here.
        </p>
      ) : null}

      <PresentToggle
        presenting={presenting}
        onToggle={() => {
          onPresentingChange(!presenting);
        }}
      />

      <LockToggle
        locked={classroom.locked}
        busy={busy}
        onToggle={() => {
          send(setClassLocked(board, !classroom.locked));
        }}
      />
    </section>
  );
}
